import { createEffect, createMemo, createSignal, createUniqueId, onCleanup, onMount, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import { useFormContext } from '~/components/Form'
import Icon from '~/components/Icon'
import { useFieldError } from '~/hooks/useFieldError'
import type { ColorFormatType, ColorPickerPropsType, HslaType } from '~/types/ColorPicker'
import { controlSize } from '~/utils/controlSize'

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h = ((h % 360) + 360) % 360
  s = clamp(s, 0, 100) / 100
  l = clamp(l, 0, 100) / 100
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0,
    g = 0,
    b = 0
  if (h < 60) {
    r = c
    g = x
  } else if (h < 120) {
    r = x
    g = c
  } else if (h < 180) {
    g = c
    b = x
  } else if (h < 240) {
    g = x
    b = c
  } else if (h < 300) {
    r = x
    b = c
  } else {
    r = c
    b = x
  }
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)]
}

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l * 100]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60
  else if (max === g) h = ((b - r) / d + 2) * 60
  else h = ((r - g) / d + 4) * 60
  return [h, s * 100, l * 100]
}

const rgbToHex = (r: number, g: number, b: number): string => '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')

const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
  if (!m) return null
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
}

const parseColor = (color: string): HslaType | null => {
  if (!color) return null
  const hex = color.match(/^#?[0-9a-f]{6}$/i)
  if (hex) {
    const rgb = hexToRgb(hex[0].startsWith('#') ? hex[0] : '#' + hex[0])
    if (rgb) {
      const [h, s, l] = rgbToHsl(...rgb)
      return { h, s, l, a: 1 }
    }
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/)
  if (rgb) {
    const [h, s, l] = rgbToHsl(+rgb[1], +rgb[2], +rgb[3])
    return { h, s, l, a: rgb[4] !== undefined ? +rgb[4] : 1 }
  }
  const hsl = color.match(/^hsla?\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%(?:\s*,\s*([\d.]+))?\s*\)$/)
  if (hsl) {
    return { h: +hsl[1], s: +hsl[2], l: +hsl[3], a: hsl[4] !== undefined ? +hsl[4] : 1 }
  }
  return null
}

const formatOutput = (hsla: HslaType, format: ColorFormatType): string => {
  const [r, g, b] = hslToRgb(hsla.h, hsla.s, hsla.l)
  switch (format) {
    case 'hex':
      return rgbToHex(r, g, b)
    case 'rgb':
      return `rgb(${r}, ${g}, ${b})`
    case 'rgba':
      return `rgba(${r}, ${g}, ${b}, ${hsla.a})`
    case 'hsl':
      return `hsl(${Math.round(hsla.h)}, ${Math.round(hsla.s)}%, ${Math.round(hsla.l)}%)`
    case 'hsla':
      return `hsla(${Math.round(hsla.h)}, ${Math.round(hsla.s)}%, ${Math.round(hsla.l)}%, ${hsla.a})`
  }
}

const DEFAULT_PRESETS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e', '#000000', '#ffffff']

const ColorPicker = (props: ColorPickerPropsType) => {
  const presets = () => props.presets ?? DEFAULT_PRESETS
  const showAlpha = () => props.showAlpha !== false
  const showPresets = () => props.showPresets !== false
  const showInputs = () => props.showInputs !== false
  const format = () => props.outputFormat ?? 'hex'

  const sizeConfig = createMemo(() => controlSize(props.size))
  /** La muestra de color va una talla por debajo del alto del control. */
  const swatchClasses = createMemo(() => ({ xs: 'h-4 w-4', sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6', xl: 'h-7 w-7' })[props.size ?? 'md'] ?? 'h-5 w-5')

  const initial = createMemo(() => parseColor(props.value ?? props.defaultValue ?? '#3b82f6') ?? { h: 217, s: 91, l: 60, a: 1 })
  const [hsla, setHsla] = createSignal<HslaType>(initial())
  const [open, setOpen] = createSignal(false)
  const rId = createUniqueId()
  const gId = createUniqueId()
  const bId = createUniqueId()
  const [pos, setPos] = createSignal({ top: 0, left: 0, width: 0, above: false })

  let wrapperRef: HTMLDivElement | undefined
  let dropdownRef: HTMLDivElement | undefined
  let satAreaRef: HTMLDivElement | undefined
  let hueSliderRef: HTMLDivElement | undefined
  let alphaSliderRef: HTMLDivElement | undefined

  const rgb = createMemo(() => hslToRgb(hsla().h, hsla().s, hsla().l))
  const hex = createMemo(() => rgbToHex(rgb()[0], rgb()[1], rgb()[2]))
  const output = createMemo(() => formatOutput(hsla(), format()))

  const id = createUniqueId()
  const formCtx = useFormContext()
  const resolvedError = useFieldError(
    () => props.name,
    () => props.error
  )

  /**
   * El color vive en una señal y el input oculto sólo lo refleja, así que el
   * `<Form>` no puede escribirlo por el DOM: al siguiente render se pierde.
   */
  let hiddenRef: HTMLInputElement | undefined
  onMount(() => {
    if (!props.name || !formCtx) return

    formCtx.registerField(props.name, {
      getValue: () => output(),
      // Sin emitir `onChange`: quien escribe es el formulario, y avisarle de su
      // propio cambio lo devolvería aquí en bucle.
      setValue: value => {
        const parsed = parseColor(String(value ?? ''))
        if (parsed) setHsla(parsed)
      }
    })

    onCleanup(() => {
      if (props.name) formCtx.unregisterField(props.name)
    })
  })

  // Un oculto no dispara eventos por sí solo: sin esto el `<Form>` no se entera
  // de que el color cambió hasta el envío.
  let lastEmitted: string | null = null
  createEffect(() => {
    const current = output()
    if (!props.name || !hiddenRef || current === lastEmitted) return
    lastEmitted = current
    hiddenRef.dispatchEvent(new Event('input', { bubbles: true }))
    hiddenRef.dispatchEvent(new Event('change', { bubbles: true }))
  })

  const updateFromColor = (color: string) => {
    const parsed = parseColor(color)
    if (parsed) {
      setHsla(parsed)
      props.onChange?.(formatOutput(parsed, format()))
      props.onChangeHsla?.(parsed)
    }
  }

  const handleSatDrag = (e: MouseEvent | Touch) => {
    if (!open()) return
    const rect = satAreaRef?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const x = clamp(e.clientX - rect.left, 0, rect.width) / rect.width
    const y = clamp(e.clientY - rect.top, 0, rect.height) / rect.height
    const s = x * 100
    const l = (1 - y) * 100
    const newHsla = { ...hsla(), s, l }
    setHsla(newHsla)
    props.onChange?.(formatOutput(newHsla, format()))
    props.onChangeHsla?.(newHsla)
  }

  const handleHueDrag = (e: MouseEvent | Touch) => {
    if (!open()) return
    const rect = hueSliderRef?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const x = clamp(e.clientX - rect.left, 0, rect.width) / rect.width
    const h = x * 360
    const newHsla = { ...hsla(), h }
    setHsla(newHsla)
    props.onChange?.(formatOutput(newHsla, format()))
    props.onChangeHsla?.(newHsla)
  }

  const handleAlphaDrag = (e: MouseEvent | Touch) => {
    if (!open()) return
    const rect = alphaSliderRef?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const x = clamp(e.clientX - rect.left, 0, rect.width) / rect.width
    const a = x
    const newHsla = { ...hsla(), a }
    setHsla(newHsla)
    props.onChange?.(formatOutput(newHsla, format()))
    props.onChangeHsla?.(newHsla)
  }

  let satDragging = false
  let hueDragging = false
  let alphaDragging = false

  const onSatDown = (e: MouseEvent) => {
    e.preventDefault()
    satDragging = true
    handleSatDrag(e)
    const onMove = (ev: MouseEvent) => {
      if (satDragging) handleSatDrag(ev)
    }
    const onUp = () => {
      satDragging = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const onHueDown = (e: MouseEvent) => {
    e.preventDefault()
    hueDragging = true
    handleHueDrag(e)
    const onMove = (ev: MouseEvent) => {
      if (hueDragging) handleHueDrag(ev)
    }
    const onUp = () => {
      hueDragging = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const onAlphaDown = (e: MouseEvent) => {
    e.preventDefault()
    alphaDragging = true
    handleAlphaDrag(e)
    const onMove = (ev: MouseEvent) => {
      if (alphaDragging) handleAlphaDrag(ev)
    }
    const onUp = () => {
      alphaDragging = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  createEffect(() => {
    if (props.value !== undefined) {
      const parsed = parseColor(props.value)
      if (parsed) setHsla(parsed)
    }
  })

  const toggleOpen = () => {
    if (props.disabled) return
    if (open()) {
      setOpen(false)
      return
    }
    const rect = wrapperRef?.getBoundingClientRect()
    if (!rect) return
    const dropdownHeight = 440
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const above = spaceBelow < dropdownHeight && rect.top > spaceBelow
    setPos({
      top: above ? rect.top - dropdownHeight : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      above
    })
    setOpen(true)
  }

  onMount(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef?.contains(target)) return
      if (dropdownRef?.contains(target)) return
      setOpen(false)
    }
    const onScroll = (e: Event) => {
      const target = e.target as Node
      if (dropdownRef?.contains(target)) return
      setOpen(false)
    }
    const onResize = () => {
      setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    onCleanup(() => {
      document.removeEventListener('mousedown', onClickOutside)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    })
  })

  const satX = () => (hsla().s / 100) * 100
  const satY = () => (1 - hsla().l / 100) * 100
  const hueX = () => (hsla().h / 360) * 100
  const alphaX = () => hsla().a * 100

  return (
    <div ref={wrapperRef} class={twMerge('relative', props.class)}>
      {/* Trigger */}
      <button
        type="button"
        class={twMerge(
          'flex w-full items-center gap-2 rounded-lg border border-border bg-surface px-3 text-left transition hover:border-primary-500 focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          sizeConfig().height,
          'py-0',
          sizeConfig().text,
          resolvedError() ? 'border-danger!' : ''
        )}
        aria-describedby={`${id}-message`}
        aria-invalid={resolvedError() ? 'true' : undefined}
        onClick={toggleOpen}
        disabled={props.disabled}
        aria-haspopup="true"
        aria-expanded={open()}
      >
        <span
          class={twMerge('shrink-0 rounded-md border border-border shadow-inner', swatchClasses())}
          style={{
            'background-color': hex(),
            'background-image':
              'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
            'background-size': '6px 6px',
            'background-position': '0 0, 0 3px, 3px -3px, -3px 0px'
          }}
        />
        <span class="flex-1 truncate font-mono text-sm text-foreground">{output()}</span>
        <Icon icon="material-symbols-light:expand-more" size={18} class="text-foreground-subtle" />
      </button>
      {/* Dropdown via Portal */}
      <Show when={open()}>
        <Portal>
          <div
            ref={dropdownRef}
            class="fixed z-[1300] w-72 rounded-xl border border-border bg-surface p-4 shadow-2xl"
            style={{
              top: `${pos().top}px`,
              left: `${pos().left}px`,
              width: `${Math.max(pos().width, 288)}px`,
              'max-height': 'calc(100vh - 16px)',
              overflow: 'auto'
            }}
          >
            <div class="mb-3 flex items-center gap-3">
              <div
                class="h-12 w-12 rounded-lg border-2 border-border shadow-inner"
                style={{
                  'background-color': hex(),
                  'background-image':
                    'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                  'background-size': '8px 8px',
                  'background-position': '0 0, 0 4px, 4px -4px, -4px 0px'
                }}
              />
              <input
                type="text"
                value={output()}
                onChange={e => updateFromColor(e.currentTarget.value)}
                class="flex-1 rounded-lg border border-border bg-surface-muted px-3 py-2 font-mono text-sm text-foreground outline-none focus:border-primary-500"
              />
            </div>

            <div
              ref={satAreaRef}
              class="relative mb-3 h-40 w-full cursor-crosshair rounded-lg border border-border"
              style={{
                background: `linear-gradient(to right, #fff, hsl(${hsla().h}, 100%, 50%))`
              }}
              onMouseDown={onSatDown}
            >
              <div class="absolute inset-0 rounded-lg" style={{ background: 'linear-gradient(to top, #000, transparent)' }} />
              <div class="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg" style={{ left: `${satX()}%`, top: `${satY()}%` }} />
            </div>

            <div class="mb-3 flex items-center gap-2">
              <span class="w-8 text-xs font-medium text-foreground-subtle">Hue</span>
              <div
                ref={hueSliderRef}
                class="relative h-4 flex-1 cursor-pointer rounded-full border border-border"
                style={{
                  background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)'
                }}
                onMouseDown={onHueDown}
              >
                <div class="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg" style={{ left: `${hueX()}%` }} />
              </div>
            </div>

            <Show when={showAlpha()}>
              <div class="mb-3 flex items-center gap-2">
                <span class="w-8 text-xs font-medium text-foreground-subtle">Alpha</span>
                <div
                  ref={alphaSliderRef}
                  class="relative h-4 flex-1 cursor-pointer rounded-full border border-border"
                  style={{
                    background: `linear-gradient(to right, transparent, ${hex()}), linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
                    'background-size': '100% 100%, 8px 8px, 8px 8px, 8px 8px, 8px 8px',
                    'background-position': '0 0, 0 0, 0 4px, 4px -4px, -4px 0px'
                  }}
                  onMouseDown={onAlphaDown}
                >
                  <div class="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg" style={{ left: `${alphaX()}%` }} />
                </div>
              </div>
            </Show>

            <Show when={showInputs()}>
              <div class="mb-3 grid grid-cols-3 gap-2">
                <div>
                  <label for={rId} class="mb-1 block text-xs font-medium text-foreground-subtle">
                    R
                  </label>
                  <input
                    id={rId}
                    type="number"
                    min={0}
                    max={255}
                    value={rgb()[0]}
                    onChange={e => {
                      const r = clamp(+e.currentTarget.value, 0, 255)
                      const [h, s, l] = rgbToHsl(r, rgb()[1], rgb()[2])
                      const newHsla = { ...hsla(), h, s, l }
                      setHsla(newHsla)
                      props.onChange?.(formatOutput(newHsla, format()))
                    }}
                    class="w-full rounded-lg border border-border bg-surface-muted px-2 py-1.5 text-center text-sm text-foreground outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label for={gId} class="mb-1 block text-xs font-medium text-foreground-subtle">
                    G
                  </label>
                  <input
                    id={gId}
                    type="number"
                    min={0}
                    max={255}
                    value={rgb()[1]}
                    onChange={e => {
                      const g = clamp(+e.currentTarget.value, 0, 255)
                      const [h, s, l] = rgbToHsl(rgb()[0], g, rgb()[2])
                      const newHsla = { ...hsla(), h, s, l }
                      setHsla(newHsla)
                      props.onChange?.(formatOutput(newHsla, format()))
                    }}
                    class="w-full rounded-lg border border-border bg-surface-muted px-2 py-1.5 text-center text-sm text-foreground outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label for={bId} class="mb-1 block text-xs font-medium text-foreground-subtle">
                    B
                  </label>
                  <input
                    id={bId}
                    type="number"
                    min={0}
                    max={255}
                    value={rgb()[2]}
                    onChange={e => {
                      const b = clamp(+e.currentTarget.value, 0, 255)
                      const [h, s, l] = rgbToHsl(rgb()[0], rgb()[1], b)
                      const newHsla = { ...hsla(), h, s, l }
                      setHsla(newHsla)
                      props.onChange?.(formatOutput(newHsla, format()))
                    }}
                    class="w-full rounded-lg border border-border bg-surface-muted px-2 py-1.5 text-center text-sm text-foreground outline-none focus:border-primary-500"
                  />
                </div>
              </div>
            </Show>

            <Show when={showPresets()}>
              <div>
                <span class="mb-2 block text-xs font-medium text-foreground-subtle">Presets</span>
                <div class="grid grid-cols-6 gap-2">
                  {presets().map(color => (
                    <button type="button" class="h-8 w-8 rounded-lg border-2 border-border transition hover:scale-110" style={{ background: color }} onClick={() => updateFromColor(color)} />
                  ))}
                </div>
              </div>
            </Show>
          </div>
        </Portal>
      </Show>

      <Show when={props.name}>
        <input ref={hiddenRef} type="hidden" name={props.name} value={output()} />
      </Show>

      <FieldMessage id={`${id}-message`} size={props.size} error={resolvedError()} helperText={props.helperText} />
    </div>
  )
}

export default ColorPicker
